import { filesize } from "filesize";

/**
 * Function that validates a value is empty
 *
 * @param {string} value Value to validate
 * @returns True if value is empty, false otherwise
 */
export const isEmpty = (value) => {
  return (
    value === undefined || value === null || value === "" || value === "null"
  );
};

/**
 * Function that validates an object is empty
 *
 * @param {Object} value Object value to validate
 * @returns True if value is empty, false otherwise
 */
export const isEmptyObject = (value) => {
  return isEmpty(value) || Object.keys(value).length === 0;
};

/**
 * Function that validates a array is empty
 *
 * @param {Array} value Value to validate
 * @returns True if value is empty, false otherwise
 */
export const isEmptyList = (value) => {
  return (
    isEmpty(value) || (Array.isArray(value) && Array.from(value).length === 0)
  );
};

/**
 * Function that validates an email
 *
 * @param {string} value Value to validate
 * @returns True if value is ok, false otherwise
 */
export const isValidEmail = (value) => {
  return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(value);
};

/**
 * Function that validate the short of a String
 *
 * @param {string} value Value to validate
 * @param {number} count Number of characters to compare
 * @returns True if value is ok, false otherwise
 */
export const isTooShort = (value, count) => {
  return (
    typeof value === "string" && !isEmpty(value) && String(value).length < count
  );
};

/**
 * Function that validate the long of a String
 *
 * @param {string} value Value to validate
 * @param {number} count Number of characters to compare
 * @returns True if value is ok, false otherwise
 */
export const isTooLong = (value, count) => {
  return (
    typeof value === "string" && !isEmpty(value) && String(value).length > count
  );
};

/**
 * Function that validate that two string are the same ignoring case
 *
 * @param {string} value1 First value to validate
 * @param {string} value2 Second value to validate
 * @returns True is two values are tue same, false otherwhise
 */
export const equalsIgnoringCase = (value1, value2) => {
  if (!isEmpty(value1) && !isEmpty(value2)) {
    if (typeof value1 === "string" && typeof value2 === "string") {
      return value1.toUpperCase().localeCompare(value2.toUpperCase()) === 0;
    } else {
      return false;
    }
  } else {
    return value1 === value2;
  }
};

/**
 * Function that validate if a value is true or not
 *
 * @param {string} value
 * @returns True if the value is a truly value, false otherwise
 */
export const isTrue = (value) => {
  if (!isEmpty(value)) {
    if (typeof value === "string") {
      return (
        equalsIgnoringCase("true", value) || equalsIgnoringCase("1", value)
      );
    } else if (typeof value === "number") {
      return value === 1;
    } else if (typeof value === "boolean") {
      return value;
    } else {
      return false;
    }
  } else {
    return false;
  }
};

/**
 * Function that validate if a value is false or not
 *
 * @param {string} value
 * @returns False if the value is a falsy value, true otherwise
 */
export const isFalse = (value) => {
  return !isTrue(value);
};

/**
 * Format bytes as human-readable text.
 *
 * @param bytes Number of bytes.
 * @param config Extra configuration
 *
 * @return Formatted string.
 */
export function humanFileSize(bytes, config = {}) {
  return filesize(bytes, {
    standard: "jedec",
    round: 2,
    pad: true,
    locale: "es",
    ...config,
  });
}

/**
 * Return decimal separator
 *
 * @return decimal separator.
 */
export function getDecimalSeparator(locale) {
  const number = 1.1;
  return Intl.NumberFormat(locale)
    .formatToParts(number)
    .find((part) => part.type === "decimal").value;
}

/**
 * Return thousand separator
 *
 * @return thousand separator.
 */
export function getThousandSeparator(locale) {
  const number = 1000.1;
  return Intl.NumberFormat(locale)
    .formatToParts(number)
    .find((part) => part.type === "group").value;
}

export const HELP_TOAST_COUNT = 3;
